import {
  GitCommitHorizontal,
  History,
  LogIn,
  LogOut,
  MessageSquare,
  Pencil,
  Play,
  type LucideIcon,
} from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import { CHANGE_KIND_LABELS } from "@ide/shared";
import type { Peer, ProjectSession } from "@/collab/session";
import { Spinner } from "@/components/ui/primitives";
import { hasFeature } from "@/desktop/edition";
import { useHistory } from "@/lib/history";
import { cn, timeAgo } from "@/lib/utils";
import { useTree, useWs } from "./context";
import { useWorkspace } from "./store";

export type SessionEventKind = "join" | "leave" | "git" | "run" | "comment";

/** Algo que aconteceu no projeto enquanto você estava nele (só nesta sessão do app). */
export interface SessionEvent {
  id: number;
  at: number;
  kind: SessionEventKind;
  who: string | null;
  color?: string;
  text: string;
}

let seq = 0;
const MAX_EVENTS = 200;

export const useSessionEvents = create<{ events: SessionEvent[]; clear(): void }>((set) => ({
  events: [],
  clear: () => set({ events: [] }),
}));

export function logSessionEvent(e: Omit<SessionEvent, "id" | "at">) {
  // O mesmo evento visto por dois editores do mesmo arquivo (editor dividido) entra uma vez.
  const last = useSessionEvents.getState().events[0];
  if (last && last.text === e.text && last.who === e.who && Date.now() - last.at < 2_000) return;
  useSessionEvents.setState((s) => ({
    events: [{ ...e, id: ++seq, at: Date.now() }, ...s.events].slice(0, MAX_EVENTS),
  }));
}

/** Quem saiu e voltou em poucos segundos (troca de rede, recarregar) não gera aviso. */
const LEAVE_GRACE_MS = 5_000;
/** Ao entrar no projeto, quem já estava lá não é "entrou agora". */
const SETTLE_MS = 2_500;

const people = (peers: Peer[]) => {
  const map = new Map<string, Peer>();
  for (const p of peers) if (!p.isSelf && !map.has(p.user.id)) map.set(p.user.id, p);
  return map;
};

/** Avisos de entrada e saída (presença real da sala do projeto), registrados na aba Atividade. */
export function usePresenceNotices(session: ProjectSession) {
  useEffect(() => {
    let known = people(session.peers());
    const started = Date.now();
    const pendingLeaves = new Map<string, ReturnType<typeof setTimeout>>();
    const notify = (kind: "join" | "leave", p: Peer) => {
      const name = p.user.name.split(" ")[0] ?? p.user.name;
      logSessionEvent({
        kind,
        who: p.user.name,
        color: p.user.color,
        text: kind === "join" ? "entrou no projeto" : "saiu do projeto",
      });
      if (useWorkspace.getState().presenceNotices) {
        toast(kind === "join" ? `${name} entrou no projeto` : `${name} saiu do projeto`, { duration: 3000 });
      }
    };
    const off = session.on("presence", (peers) => {
      const now = people(peers);
      const settled = Date.now() - started > SETTLE_MS;
      for (const [id, p] of now) {
        if (known.has(id)) continue;
        const pending = pendingLeaves.get(id);
        if (pending) {
          clearTimeout(pending);
          pendingLeaves.delete(id);
        } else if (settled) notify("join", p);
      }
      for (const [id, p] of known) {
        if (now.has(id) || pendingLeaves.has(id)) continue;
        pendingLeaves.set(
          id,
          setTimeout(() => {
            pendingLeaves.delete(id);
            notify("leave", p);
          }, LEAVE_GRACE_MS),
        );
      }
      known = now;
    });
    return () => {
      off();
      pendingLeaves.forEach((t) => clearTimeout(t));
      useSessionEvents.getState().clear();
    };
  }, [session]);
}

const EVENT_ICON: Record<SessionEventKind, LucideIcon> = {
  join: LogIn,
  leave: LogOut,
  git: GitCommitHorizontal,
  run: Play,
  comment: MessageSquare,
};

function SectionTitle({ children }: { children: string }) {
  return (
    <p className="px-4 pb-1 pt-3 text-[11px] font-medium uppercase tracking-[0.08em] text-fg-subtle">
      {children}
    </p>
  );
}

/** Aba Atividade (lateral direita): o que aconteceu nesta sessão e as últimas alterações. */
export function ActivityPanel() {
  const { detail } = useWs();
  const tree = useTree();
  const events = useSessionEvents((s) => s.events);
  const withHistory = hasFeature("history") && detail.isMember;
  const history = useHistory(detail.project.id, {}, withHistory);
  const changes = (history.data?.pages[0]?.items ?? []).slice(0, 25);

  return (
    <div className="h-full overflow-y-auto pb-4">
      {/* Quem está aqui agora fica na aba Pessoas: aqui, só o que aconteceu. */}
      <SectionTitle>Nesta sessão</SectionTitle>
      {events.length ? (
        <ul>
          {events.slice(0, 40).map((e) => {
            const Icon = EVENT_ICON[e.kind];
            return (
              <li key={e.id} className="flex items-start gap-2.5 px-4 py-1 text-[12.5px]">
                <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-subtle" />
                <span className="min-w-0 flex-1 text-fg-muted">
                  {e.who ? <span className="font-medium text-fg">{e.who} </span> : null}
                  {e.text}
                </span>
                <span className="shrink-0 text-[11px] text-fg-subtle">{timeAgo(e.at)}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="px-4 py-1 text-[12.5px] text-fg-subtle">
          Entradas, saídas, commits e execuções aparecem aqui enquanto você estiver no projeto.
        </p>
      )}

      {withHistory ? (
        <>
          <SectionTitle>Alterações recentes</SectionTitle>
          {history.isLoading ? (
            <div className="flex justify-center py-3">
              <Spinner />
            </div>
          ) : changes.length ? (
            <ul>
              {changes.map((c) => {
                const author = c.authors[0];
                const exists = tree.nodes.has(c.fileId);
                return (
                  <li key={c.id}>
                    <button
                      disabled={!exists || c.nodeType !== "file"}
                      onClick={() => useWorkspace.getState().openFile(c.fileId)}
                      className="flex w-full items-start gap-2.5 px-4 py-1 text-left text-[12.5px] enabled:hover:bg-bg-muted"
                    >
                      <Pencil className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-subtle" />
                      <span className="min-w-0 flex-1">
                        <span className="font-medium text-fg">{author?.name ?? "Alguém"}</span>{" "}
                        <span className="text-fg-muted">{CHANGE_KIND_LABELS[c.kind]}</span>{" "}
                        <span className={cn("break-all text-fg", !exists && "line-through opacity-70")}>
                          {c.path}
                        </span>
                        {c.linesAdded || c.linesRemoved ? (
                          <span className="ml-1 font-mono text-[11px] text-fg-subtle">
                            +{c.linesAdded} −{c.linesRemoved}
                          </span>
                        ) : null}
                      </span>
                      <span className="shrink-0 text-[11px] text-fg-subtle">
                        {c.live ? "agora" : timeAgo(c.updatedAt)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="px-4 py-1 text-[12.5px] text-fg-subtle">Nenhuma alteração registrada ainda.</p>
          )}
          <button
            onClick={() => useWorkspace.getState().setPanel("history")}
            className="mx-4 mt-2 flex items-center gap-1.5 text-[12px] text-fg-muted hover:text-fg"
          >
            <History className="h-3.5 w-3.5" /> Histórico completo
          </button>
        </>
      ) : null}
    </div>
  );
}
